import React from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import moment from 'moment';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import AdminOverview from '../../component/AdminOverview';
import { PlanningTicket, PlanningTicketStore, Milestone, MilestoneStore } from '../../store/planning';
import { api } from '../../store/Base';
import { OutlineIcon } from './shared/OutlineIcon';
import RemarkupContent from './shared/RemarkupContent';

// [T54364] Light theme palette matching MilestoneDetailOverview
const C = {
    bg: '#f5f6f7', surface: '#ffffff', border: '#e0e3e8', borderStrong: '#c5cad2',
    textPrimary: '#1a1d23', textSecondary: '#5c6470', textMuted: '#9099a8',
    accent: '#2185d0', accentLight: '#e8f3fb',
    success: '#21ba45', successLight: '#eafbef',
    warning: '#f0a721', warningLight: '#fef5e7',
    danger: '#db2828', dangerLight: '#fde8e8',
    sidebarBg: '#f8f9fa',
    statusTodo: '#6b7280', statusInProgress: '#2563eb', statusReview: '#d97706',
    statusDone: '#16a34a', statusBlocked: '#dc2626',
    rowStripe: '#fafbfc',
};

const STATUS_DOT: Record<string, string> = {
    todo: C.statusTodo, in_progress: C.statusInProgress, review: C.statusReview,
    done: C.statusDone, blocked: C.statusBlocked,
};
const STATUS_LABEL: Record<string, string> = {
    todo: 'To Do', in_progress: 'In Progress', review: 'In Review',
    done: 'Done', blocked: 'Blocked',
};

function hours(n: number | null | undefined): string {
    const v = Math.round((Number(n) || 0) * 10) / 10;
    // Trim trailing .0 for whole numbers, keep one decimal otherwise
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

export const Shell = styled.div`
    display: flex;
    flex-direction: column;
    /* [T54191] The app shell now has a real viewport height (see index.js),
       so 100% resolves properly and the table scrolls internally instead of
       the page. */
    height: 100%;
    background: ${C.bg};
    overflow: hidden;
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: ${C.surface};
    border-bottom: 1px solid ${C.border};
    flex-shrink: 0;
`;

const TopTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${C.textPrimary};
    white-space: nowrap;
`;
const TopSub = styled.div`
    font-size: 12px;
    color: ${C.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Btn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid ${C.border};
    background: ${C.surface};
    color: ${C.textPrimary};
    border-radius: 6px;
    padding: 5px 12px;
    font-size: 12.5px;
    cursor: pointer;
    white-space: nowrap;
    &:hover { background: ${C.accentLight}; border-color: ${C.accent}; }
`;

const SyncBtn = styled(Btn)`
    color: #6b46c1;
    border-color: #cabcf2;
    &:hover { background: #f3effc; border-color: #6b46c1; }
`;

const Body = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
`;

const Main = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: ${C.bg};
`;

const KpiStrip = styled.div`
    display: flex;
    gap: 12px;
    padding: 12px 16px 4px;
    flex-wrap: wrap;
    flex-shrink: 0;
`;

const Kpi = styled.div`
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 8px;
    padding: 8px 14px;
    min-width: 130px;
`;
const KpiLabel = styled.div`
    font-size: 11px;
    color: ${C.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 2px;
`;
const KpiValue = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${C.textPrimary};
`;

const ProgressTrack = styled.div`
    height: 8px;
    border-radius: 4px;
    background: #e6e9ee;
    overflow: hidden;
    margin-top: 6px;
`;
const ProgressFill = styled.div`
    height: 100%;
    border-radius: 4px;
    background: ${(p: any) => p.color || C.accent};
    width: ${(p: any) => Math.min(100, Math.max(0, Number(p.pct) || 0))}%;
`;

const TableWrap = styled.div`
    flex: 1;
    overflow: auto;
    padding: 8px 16px 16px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 8px;
    font-size: 13px;
`;

const Th = styled.th`
    text-align: left;
    padding: 9px 12px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${C.textSecondary};
    border-bottom: 1px solid ${C.border};
    background: ${C.sidebarBg};
    position: sticky;
    top: 0;
    z-index: 1;
    white-space: nowrap;
    cursor: ${(p: any) => (p.sortable ? 'pointer' : 'default')};
    &:hover { color: ${C.textPrimary}; }
`;

const Td = styled.td`
    padding: 8px 12px;
    border-bottom: 1px solid ${C.border};
    color: ${C.textPrimary};
    vertical-align: middle;
`;
const Tr = styled.tr`
    background: ${(p: any) => (p.stripe ? C.rowStripe : C.surface)};
    &:hover { background: ${C.accentLight}; }
`;

const TicketTitle = styled.div`
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 220px;
`;

// [T54255] The ticket title as a button — opens the description popup.
const TitleButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 500;
    color: ${C.textPrimary};
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    &:hover { color: ${C.accent}; text-decoration: underline; }
`;

const StatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 10px;
    padding: 2px 9px;
    font-size: 11.5px;
    font-weight: 600;
    color: ${(p: any) => p.color};
    background: ${(p: any) => p.bg};
    white-space: nowrap;
`;

const ColPill = styled.span`
    display: inline-block;
    border-radius: 10px;
    padding: 2px 9px;
    font-size: 11.5px;
    background: ${C.accentLight};
    color: ${C.accent};
    border: 1px solid #bcd8f0;
    white-space: nowrap;
`;
const ColMuted = styled.span`
    color: ${C.textMuted};
    font-size: 12px;
`;

const HoursCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 150px;
`;
const HoursNumbers = styled.div`
    font-variant-numeric: tabular-nums;
    font-size: 12.5px;
`;
const MiniBar = styled.div`
    height: 5px;
    border-radius: 3px;
    background: #e6e9ee;
    overflow: hidden;
`;
const MiniBarFill = styled.div`
    height: 100%;
    background: ${(p: any) => p.color};
    width: ${(p: any) => Math.min(100, Math.max(0, Number(p.pct) || 0))}%;
`;


const EstNote = styled.span`
    color: ${C.textSecondary};
`;
const OverNote = styled.span`
    color: ${C.danger};
`;
const NoEstNote = styled.span`
    color: ${C.warning};
`;
const ScheduledNote = styled.span`
    color: ${C.textSecondary};
    font-size: 12px;
`;
const PlainLink = styled(Link)`
    text-decoration: none;
`;
const FlexSpacer = styled.span`
    flex: 1;
`;
const SyncMsg = styled.span`
    color: ${(p: any) => (p.kind === 'success' ? C.success : C.danger)};
    font-size: 12.5px;
`;
const EmptyCell = styled.td`
    text-align: center;
    color: ${C.textMuted};
    padding: 24px;
`;

const TnumLink = styled.a`
    color: ${C.accent};
    text-decoration: none;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    &:hover { text-decoration: underline; }
`;

const AssigneeChip = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${C.textSecondary};
    font-size: 12.5px;
    white-space: nowrap;
`;
const AvatarMini = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${(p: any) => p.color};
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
`;

// ── [T54255] Ticket description popup ──────────────────────────────────────

const PopupBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(23, 26, 33, 0.4);
    z-index: 90;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
`;

const Popup = styled.div`
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 10px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
    width: 640px;
    max-width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const PopupHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid ${C.border};
    background: ${C.sidebarBg};
    flex-shrink: 0;
`;

const PopupTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    color: ${C.textPrimary};
    margin: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const PopupClose = styled.button`
    background: none;
    border: none;
    font-size: 18px;
    color: ${C.textMuted};
    cursor: pointer;
    padding: 2px 8px;
    line-height: 1;
    &:hover { color: ${C.textPrimary}; }
`;

const PopupBody = styled.div`
    padding: 16px 20px;
    overflow-y: auto;
    flex: 1;
`;

const DescEmpty = styled.div`
    color: ${C.textMuted};
    font-size: 13px;
    font-style: italic;
`;

const DescEditArea = styled.textarea`
    width: 100%;
    min-height: 200px;
    padding: 8px 10px;
    background: #f8f9fa;
    border: 1px solid ${C.border};
    border-radius: 4px;
    color: ${C.textPrimary};
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    &:focus { outline: none; border-color: ${C.accent}; background: #fff; }
`;

const DescToggle = styled.button<{ save?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: 1px solid ${(p: any) => (p.save ? C.success : C.border)};
    background: ${C.surface};
    color: ${(p: any) => (p.save ? C.success : C.textSecondary)};
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    &:hover { background: ${C.accentLight}; border-color: ${C.accent}; color: ${C.accent}; }
`;

const SaveHint = styled.span<{ saved?: boolean }>`
    font-size: 11px;
    color: ${(p: any) => (p.saved ? C.success : C.textMuted)};
`;

function colorFor(s: string): string {
    const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

type SortKey = 'order' | 'title' | 'status' | 'column' | 'spent' | 'estimate' | 'assignee';

@observer
export default class MilestoneStatusOverview extends AdminOverview {
    myFilterKey = 'planning-milestone-status';
    settings: any[] = []; buttons: any[] = []; toolbar: any[] = []; filters: any[] = [];

    store = new PlanningTicketStore({ relations: ['milestone', 'assignee'], limit: false });
    milestoneStore = new MilestoneStore({
        relations: ['project.company', 'budget'],
        limit: false,
        params: { include_annotations: 'spent_hours' },
    });

    get milestoneId(): number { return Number((this.props as any).match?.params?.id); }

    @observable milestone: Milestone | null = null;
    @observable syncing = false;
    @observable syncMsg: { text: string; kind: 'success' | 'error' } | null = null;
    @observable sortBy: SortKey = 'order';
    @observable sortAsc = true;
    // [T54255] Ticket description popup state
    @observable descTicket: PlanningTicket | null = null;
    @observable descEditing = false;
    @observable descDraft = '';
    @observable descSaving = false;

    async componentDidMount() {
        if (this.milestoneId) {
            try {
                const ms = new Milestone({ id: this.milestoneId } as any, { relations: ['project.company', 'budget'] } as any) as Milestone;
                await ms.fetch();
                this.milestone = ms;
            } catch (e) { console.warn('milestone fetch failed', e); }
            try { await this.milestoneStore.fetch(); } catch (e) { console.warn('milestones fetch failed', e); }
        }
        await this.fetch();
    }

    async fetch() {
        await this.store.fetch().catch((e: any) => console.warn('ticket fetch failed', e));
    }

    get tickets(): PlanningTicket[] {
        const all = (this.store.models as PlanningTicket[]) || [];
        return all.filter((tk: any) => Number(tk.milestone?.id ?? tk.milestone) === this.milestoneId);
    }

    get sortedTickets(): PlanningTicket[] {
        const dir = this.sortAsc ? 1 : -1;
        const key = this.sortBy;
        const list = [...this.tickets];
        list.sort((a: any, b: any) => {
            switch (key) {
                case 'title': return a.title.localeCompare(b.title) * dir;
                case 'status': return (a.status || '').localeCompare(b.status || '') * dir;
                case 'column': return (a.phabricatorColumn || '').localeCompare(b.phabricatorColumn || '') * dir;
                case 'spent': return ((Number(a.spentHours) || 0) - (Number(b.spentHours) || 0)) * dir;
                case 'estimate': return ((Number(a.estimatedHours) || 0) - (Number(b.estimatedHours) || 0)) * dir;
                case 'assignee': {
                    const an = (a.assignee?.firstName || '') + ' ' + (a.assignee?.lastName || '');
                    const bn = (b.assignee?.firstName || '') + ' ' + (b.assignee?.lastName || '');
                    return an.trim().localeCompare(bn.trim()) * dir;
                }
                default: return ((a.order ?? 0) - (b.order ?? 0)) * dir;
            }
        });
        return list;
    }

    // ── summary numbers ──
    get totalTickets(): number { return this.tickets.length; }
    get doneTickets(): number { return this.tickets.filter((tk: any) => tk.status === 'done').length; }
    get totalEstimated(): number {
        return Math.round(this.tickets.reduce((s: number, tk: any) => s + (Number(tk.estimatedHours) || 0), 0) * 10) / 10;
    }
    get totalSpent(): number {
        return Math.round(this.tickets.reduce((s: number, tk: any) => s + (Number(tk.spentHours) || 0), 0) * 10) / 10;
    }
    // Hours actually registered on this sub project (annotation, all entries)
    get registeredHours(): number {
        return Math.round((Number((this.milestone as any)?.spentHours) || 0) * 10) / 10;
    }
    get capHours(): number | null { return (this.milestone as any)?.budgetHours ?? null; }
    get budgetSpent(): number | null {
        const b = (this.milestone as any)?.budget;
        return b && b.id ? Number(b.spentHours) || 0 : null;
    }

    @action toggleSort = (key: SortKey) => {
        if (this.sortBy === key) { this.sortAsc = !this.sortAsc; }
        else { this.sortBy = key; this.sortAsc = true; }
    };

    // ── [T54255] Ticket description popup ──

    @action openDescription = (tk: PlanningTicket) => {
        this.descTicket = tk;
        this.descEditing = false;
        this.descDraft = (tk as any).description || '';
    };

    @action closeDescription = () => {
        this.descTicket = null;
        this.descEditing = false;
        this.descDraft = '';
    };

    @action toggleDescEdit = () => {
        // Entering edit mode (re)loads the raw text; leaving discards the draft.
        if (!this.descEditing) {
            this.descDraft = (this.descTicket as any)?.description || '';
        }
        this.descEditing = !this.descEditing;
    };

    @action handleDescDraftChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.descDraft = e.target.value;
    };

    /** PATCH the raw description; the backend pushes it to Phabricator and
     * re-renders description_html (remarkup.process) server-side. */
    @action saveDescription = async () => {
        const tk: any = this.descTicket;
        if (!tk?.id || this.descSaving) return;
        this.descSaving = true;
        try {
            await api.patch('planning_ticket/' + tk.id + '/', { description: this.descDraft });
            tk.description = this.descDraft;
            this.descEditing = false;
            await this.fetch();
        } catch (e: any) {
            console.error('saveDescription error:', e);
        } finally {
            this.descSaving = false;
        }
    };

    @action handleDescKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Ctrl/Cmd+Enter saves; Escape closes the popup.
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            this.saveDescription();
        } else if (e.key === 'Escape') {
            e.stopPropagation();
            this.closeDescription();
        }
    };

    @action syncPhabricator = async () => {
        if (!this.milestoneId) return;
        this.syncing = true;
        this.syncMsg = null;
        try {
            const res = await api.get(`subproject/${this.milestoneId}/sync_phabricator/`);
            const data = (res as any)?.data ?? res ?? {};
            this.syncMsg = {
                text: `Synced: ${data.created || 0} created, ${data.updated || 0} updated, ${data.skipped || 0} skipped`,
                kind: 'success',
            };
            await this.fetch();
        } catch (e: any) {
            console.error('syncPhabricator error:', e);
            this.syncMsg = { text: 'Sync failed: ' + (e?.message || 'unknown error'), kind: 'error' };
        } finally {
            this.syncing = false;
        }
    };

    sortIndicator(key: SortKey): string {
        if (this.sortBy !== key) return '';
        return this.sortAsc ? ' ▲' : ' ▼';
    }

    renderTicketRow(tk: PlanningTicket, idx: number) {
        const t = tk as any;
        const spent = Math.max(0, Number(t.spentHours) || 0);
        const est = Number(t.estimatedHours) || 0;
        let pct = 0;
        if (est > 0) pct = (spent / est) * 100;
        else if (spent > 0) pct = 100;
        const over = est > 0 && spent > est;
        // Over-budget always wins (red), even for done tickets; otherwise
        // status-tinted. Spent hours with no estimate at all (est === 0)
        // shows a neutral full bar with the spent hours in the label.
        let barColor = '#cbd5e1';
        if (over) barColor = C.statusBlocked;
        else if (t.status === 'done') barColor = C.statusDone;
        else if (t.status === 'review') barColor = C.statusReview;
        else if (spent > 0) barColor = C.statusInProgress;
        const stColor = STATUS_DOT[t.status] || C.textMuted;
        const assignee = t.assignee;
        const aname = assignee && assignee.id
            ? `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim()
            : null;

        return (
            <Tr key={t.id} stripe={idx % 2 === 1} data-test-status-ticket={t.id}>
                <Td>
                    <TicketTitle>
                        {t.phabricatorTaskNumber ? (
                            <TnumLink
                                href={`https://phabricator.codeyellow.nl/T${t.phabricatorTaskNumber}`}
                                target="_blank"
                                rel="noreferrer"
                                data-test-ticket-tnum={t.id}
                            >
                                T{t.phabricatorTaskNumber}
                            </TnumLink>
                        ) : null}
                        {/* [T54255] Clicking the title opens the description popup. */}
                        <TitleButton
                            type="button"
                            onClick={() => this.openDescription(tk)}
                            data-test-ticket-title={t.id}
                        >
                            {t.title}
                        </TitleButton>
                    </TicketTitle>
                </Td>
                <Td>
                    <StatusPill color={stColor} bg={`${stColor}1a`} data-test-ticket-status={t.id}>
                        {STATUS_LABEL[t.status] || t.status}
                    </StatusPill>
                </Td>
                <Td>
                    {t.phabricatorColumn
                        ? <ColPill data-test-ticket-column={t.id}>{t.phabricatorColumn}</ColPill>
                        : <ColMuted>—</ColMuted>}
                </Td>
                <Td>
                    <HoursCell data-test-ticket-hours={t.id}>
                        <HoursNumbers>
                            <strong>{hours(spent)}</strong>
                            <EstNote> / {hours(est)} h</EstNote>
                            {over && <OverNote> ({hours(spent - est)} over)</OverNote>}
                            {!over && est === 0 && spent > 0 && <NoEstNote> (no estimate)</NoEstNote>}
                        </HoursNumbers>
                        <MiniBar>
                            <MiniBarFill
                                color={barColor}
                                pct={pct}
                                data-test-ticket-hours-fill={t.id}
                            />
                        </MiniBar>
                    </HoursCell>
                </Td>
                <Td>
                    {assignee && assignee.id ? (
                        <AssigneeChip>
                            <AvatarMini color={colorFor(aname || '?')}>
                                {(assignee.firstName || '?').charAt(0)}{(assignee.lastName || '').charAt(0)}
                            </AvatarMini>
                            {aname}
                        </AssigneeChip>
                    ) : <ColMuted>Unassigned</ColMuted>}
                </Td>
                <Td>
                    <ScheduledNote>
                        {t.scheduledStart ? moment(t.scheduledStart).format('MMM D') : '—'}
                        {' → '}
                        {t.scheduledEnd ? moment(t.scheduledEnd).format('MMM D') : '—'}
                    </ScheduledNote>
                </Td>
            </Tr>
        );
    }

    render() {
        const ms = this.milestone as any;
        const tickets = this.sortedTickets;
        const donePct = this.totalTickets > 0 ? (this.doneTickets / this.totalTickets) * 100 : 0;
        const budget = ms?.budget;
        const capPct = this.capHours ? Math.min(100, (this.registeredHours / this.capHours) * 100) : 0;

        return (
            <Shell data-test-milestone-status>
                <TopBar>
                    <PlainLink to={`/planning/milestone/${this.milestoneId}/overview`}>
                        <Btn data-test-back-to-planner>← Planner</Btn>
                    </PlainLink>
                    <div>
                        <TopTitle data-test-status-title>{ms?.name || 'Milestone status'}</TopTitle>
                        <TopSub>
                            {ms?.project?.name || ''}
                            {ms?.project?.company?.name ? ` · ${ms.project.company.name}` : ''}
                        </TopSub>
                    </div>
                    <FlexSpacer />
                    {this.syncMsg && (
                        <SyncMsg
                            data-test-sync-msg
                            kind={this.syncMsg.kind}
                        >
                            {this.syncMsg.text}
                        </SyncMsg>
                    )}
                    <SyncBtn
                        data-test-sync-phab-btn
                        onClick={this.syncPhabricator}
                        disabled={this.syncing}
                    >
                        <OutlineIcon name="sync" color="#6b46c1" size={13} />
                        {this.syncing ? 'Syncing…' : 'Sync Phabricator'}
                    </SyncBtn>
                </TopBar>

                <Body>
                    <Main>
                        <KpiStrip>
                            <Kpi>
                                <KpiLabel>Tickets done</KpiLabel>
                                <KpiValue data-test-kpi-done>{this.doneTickets}/{this.totalTickets}</KpiValue>
                                <ProgressTrack>
                                    <ProgressFill color={C.statusDone} pct={donePct} data-test-kpi-done-fill />
                                </ProgressTrack>
                            </Kpi>
                            <Kpi>
                                <KpiLabel>Estimated hours</KpiLabel>
                                <KpiValue data-test-kpi-estimated>{hours(this.totalEstimated)} h</KpiValue>
                            </Kpi>
                            <Kpi>
                                <KpiLabel>Spent hours (tickets)</KpiLabel>
                                <KpiValue data-test-kpi-spent>{hours(this.totalSpent)} h</KpiValue>
                            </Kpi>
                            <Kpi>
                                <KpiLabel>Registered on sub project</KpiLabel>
                                <KpiValue data-test-kpi-registered>{hours(this.registeredHours)} h</KpiValue>
                                {this.capHours !== null && (
                                    <ProgressTrack>
                                        <ProgressFill
                                            color={this.registeredHours > (this.capHours || 0) ? C.danger : C.accent}
                                            pct={capPct}
                                            data-test-kpi-cap-fill
                                        />
                                    </ProgressTrack>
                                )}
                            </Kpi>
                            {this.capHours !== null && (
                                <Kpi>
                                    <KpiLabel>Sub project cap</KpiLabel>
                                    <KpiValue data-test-kpi-cap>{hours(this.capHours)} h</KpiValue>
                                </Kpi>
                            )}
                            {budget && budget.id && (
                                <Kpi>
                                    <KpiLabel>Budget · {budget.name || 'budget'}</KpiLabel>
                                    <KpiValue data-test-kpi-budget>
                                        {hours(budget.spentHours)} / {hours(budget.budgetHours)} h
                                    </KpiValue>
                                </Kpi>
                            )}
                        </KpiStrip>

                        <TableWrap>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th sortable onClick={() => this.toggleSort('title')}>
                                            Ticket{this.sortIndicator('title')}
                                        </Th>
                                        <Th sortable onClick={() => this.toggleSort('status')}>
                                            Status{this.sortIndicator('status')}
                                        </Th>
                                        <Th sortable onClick={() => this.toggleSort('column')}>
                                            Phabricator column{this.sortIndicator('column')}
                                        </Th>
                                        <Th sortable onClick={() => this.toggleSort('spent')}>
                                            Spent / expected{this.sortIndicator('spent')}
                                        </Th>
                                        <Th sortable onClick={() => this.toggleSort('assignee')}>
                                            Assignee{this.sortIndicator('assignee')}
                                        </Th>
                                        <Th>Scheduled</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickets.length === 0 && (
                                        <tr>
                                            <Td colSpan={6} as={EmptyCell}>
                                                No tickets{ms?.phabricatorProjectPhid ? ' — try Sync Phabricator' : ''}.
                                            </Td>
                                        </tr>
                                    )}
                                    {tickets.map((tk, i) => this.renderTicketRow(tk, i))}
                                </tbody>
                            </Table>
                        </TableWrap>
                    </Main>
                </Body>

                {/* [T54255] Ticket description popup */}
                {this.descTicket && this.renderDescriptionPopup()}
            </Shell>
        );
    }

    renderDescriptionPopup() {
        const t = this.descTicket as any;
        const hasHtml = !!(t.descriptionHtml && t.descriptionHtml.trim());
        return (
            <PopupBackdrop
                data-test-ticket-desc-popup
                onClick={this.closeDescription}
            >
                <Popup onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <PopupHeader>
                        {t.phabricatorTaskNumber ? (
                            <TnumLink
                                href={`https://phabricator.codeyellow.nl/T${t.phabricatorTaskNumber}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                T{t.phabricatorTaskNumber}
                            </TnumLink>
                        ) : null}
                        <PopupTitle data-test-ticket-desc-title>{t.title}</PopupTitle>
                        {this.descEditing ? (
                            <>
                                <SaveHint saved={false}>
                                    {this.descSaving ? 'Saving…' : 'Ctrl+Enter to save'}
                                </SaveHint>
                                <DescToggle
                                    onClick={this.toggleDescEdit}
                                    data-test-ticket-desc-cancel
                                >
                                    Cancel
                                </DescToggle>
                                <DescToggle
                                    onClick={this.saveDescription}
                                    disabled={this.descSaving}
                                    data-test-ticket-desc-save
                                    save
                                >
                                    {this.descSaving ? 'Saving…' : 'Save'}
                                </DescToggle>
                            </>
                        ) : (
                            <DescToggle
                                onClick={this.toggleDescEdit}
                                data-test-ticket-desc-edit
                            >
                                <OutlineIcon name="pencil" color="#5c6470" size={12} />
                                Edit
                            </DescToggle>
                        )}
                        <PopupClose
                            onClick={this.closeDescription}
                            data-test-ticket-desc-close
                        >
                            ×
                        </PopupClose>
                    </PopupHeader>
                    <PopupBody>
                        {this.renderPopupBody(hasHtml)}
                    </PopupBody>
                </Popup>
            </PopupBackdrop>
        );
    }

    /** [T54255] Popup body: raw editor while editing, else the rendered
     * description (or the empty note). Named helper instead of a nested
     * ternary (lint). */
    renderPopupBody(hasHtml: boolean) {
        const t = this.descTicket as any;
        if (this.descEditing) {
            return (
                <DescEditArea
                    value={this.descDraft}
                    onChange={this.handleDescDraftChange}
                    onKeyDown={this.handleDescKeyDown}
                    data-test-ticket-desc-editor
                />
            );
        }
        if (hasHtml) {
            return (
                <RemarkupContent
                    html={t.descriptionHtml}
                    data-test="ticket-desc-rendered"
                />
            );
        }
        return (
            <DescEmpty data-test-ticket-desc-empty>
                No description.
            </DescEmpty>
        );
    }
}
